import { Fonts, Mode, type SceneEditorButtons } from '@/enums';
import { definePrefs, type Pref, pref } from './define';
import { migrateOldPreferences } from './migrate';

const prefDefs = {
    side_bar: pref(true),
    last_page: pref(''),
    plan_menu: pref('1'),
    plan_episode_menu: pref('1'),
    episode_plan_menu: pref('2'),
    episode_id: pref<number | null>(null, { legacy: 'raw-num' }),
    hide_tags: pref(false),
    cork: pref(false),
    mode: pref<Mode>(Mode.light),
    blocks_font: pref<Fonts>(Fonts.courier),
    editor_episode_id: pref<number | null>(null, { legacy: 'raw-num', from: 'editor_episode_id' }),
    editor_scene: pref<{ id: number | null; act: number | null }>({ id: null, act: null }, { from: 'editor_scene' }),
    editor_sidebar: pref<SceneEditorButtons | null>(null, { from: 'editor_sidebar' }),
    show_navigator: pref(true, { legacy: 'raw-bool', from: 'editor_show_navigator' }),
    show_editor: pref(true, { legacy: 'raw-bool', from: 'editor_show_editor' }),
    show_logline: pref(true, { legacy: 'raw-bool', from: 'editor_show_logline' }),
    show_pep: pref(false, { legacy: 'raw-bool', from: 'editor_show_pep' }),
    /** Draft blocks: "hide drafts" masked-view filter (per-device, mirrored
     *  into the engine via `setDraftMask`). */
    hide_drafts: pref(false),
    /** «Заметки»: the «Скрыть заметки» masked-view filter (per-device, mirrored
     *  into the engine via `setNoteMask`). Its own key, independent of
     *  `hide_drafts` and of the expanded-quote set. */
    hide_notes: pref(false),
    /** «Логлайны» (issue #1833): hides every SCENE logline row in the Fountain
     *  paper (per-device, mirrored into the engine via `setLoglineMask`). A
     *  hidden logline still reveals while its scene heading holds focus — same
     *  focus-group reveal an empty logline has always had. The act's and the
     *  episode's own loglines answer to their own switches below (#1841-05). */
    hide_loglines: pref(false),
    /** «Сюжеты» (issue #1833): shows each SCENE's editable synopsis under its
     *  logline in the Fountain paper — the same scene_synopsis atom the
     *  Navigator edits. Opt-in, so it defaults off. */
    show_synopses: pref(false),
    /** «Акты» / «Эпизоды» (#1841 item 5): the act's and the episode's own row
     *  group on the sheet, whole — name row, logline, plot and the notes hung
     *  under it. One switch per entity, because the two groups are read for
     *  different reasons and hiding one says nothing about the other. */
    hide_act_rows: pref(false),
    hide_episode_rows: pref(false),
    /** «Логлайны актов» / «Логлайны эпизодов» (#1841 item 5): one ROW inside
     *  the group above. The name row is never theirs — it is the structural
     *  marker, and only the entity switch takes it. */
    hide_act_loglines: pref(false),
    hide_episode_loglines: pref(false),
    /** «Сюжеты актов» / «Сюжеты эпизодов» (#1841 item 5): opt-IN layers, like
     *  `show_synopses` is for the scene — the switch says "show", so a plot
     *  row is off the sheet until it is asked for. */
    show_act_plots: pref(false),
    show_episode_plots: pref(false),
};

migrateOldPreferences(prefDefs);

export const prefs = definePrefs(prefDefs);

export type PreferenceSchema = {
    [K in keyof typeof prefs]: (typeof prefs)[K] extends Pref<infer T> ? T : never;
};

export const PREFERENCE_DEFAULTS: PreferenceSchema = (() => {
    const out: Record<string, unknown> = {};
    for (const [key, p] of Object.entries(prefs)) {
        out[key] = (p as Pref<unknown>).defaultValue;
    }
    return out as PreferenceSchema;
})();
